// Opt-in resolver registry for the mode-level urgency indicator (D1).
//
// Doctrine: see `docs/planning/style_doctrine.md` §"Mode-level urgency
// indicators (NEW — from D1)". Each mode opts in by adding an entry to
// `MODE_URGENCY_RESOLVERS`; the chrome surfaces (`SidebarModeList`,
// `MobileBottomTabBar`) consume the result via `useModeUrgency(mode)`.
//
// Opt-ins:
// - Sales Console: open follow-ups due soon or overdue.
//
// The ads-goal urgency badge previously lived on the Ads Assistant rail icon
// (D2). It now lives on the shell-level ActivityFloatingDock instead — see
// `docs/planning/org_activity_top_level_mode.md`. The dock derives its
// status directly from `WorkspaceUrgencyContext.adsActivity` using
// `resolveAdsHeaderActivityPill`, so no entry is needed here.

import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";

import { SALES_CONSOLE_DUE_SOON_MS, SALES_CONSOLE_DUE_TODAY_AMBER } from "./modeUrgencyDefaults";
import {
  NO_URGENCY,
  type ModeUrgency,
  type ModeUrgencyContext,
  type ModeUrgencyResolver,
} from "./modeUrgencyTypes";
import { resolveLeadsFollowUpUrgency } from "@/lib/lead/upcomingFollowUpAttention";

function pluralize(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

const salesConsoleResolver: ModeUrgencyResolver = (ctx: ModeUrgencyContext): ModeUrgency => {
  const summary = resolveLeadsFollowUpUrgency(ctx.leads, ctx.nowMs, SALES_CONSOLE_DUE_SOON_MS);
  if (summary.tier === "none" || summary.count === 0) return NO_URGENCY;

  if (summary.tier === "overdue") {
    const reason = `${pluralize(summary.count, "overdue follow-up", "overdue follow-ups")}`;
    return {
      active: true,
      tone: "red",
      reason,
      ariaLabel: `Sales Console — ${reason}. Open to handle.`,
    };
  }

  if (summary.tier === "due_soon") {
    const reason = `${pluralize(summary.count, "follow-up", "follow-ups")} due within 30 minutes`;
    return {
      active: true,
      tone: "amber",
      reason,
      ariaLabel: `Sales Console — ${reason}.`,
    };
  }

  // due_today
  if (!SALES_CONSOLE_DUE_TODAY_AMBER) return NO_URGENCY;
  const reason = `${pluralize(summary.count, "follow-up", "follow-ups")} due later today`;
  return {
    active: true,
    tone: "amber",
    reason,
    ariaLabel: `Sales Console — ${reason}.`,
  };
};

/**
 * Mode-registry opt-in: only modes that appear here will ever drive a badge.
 * Missing modes resolve to `{ active: false }` via `useModeUrgency`.
 */
export const MODE_URGENCY_RESOLVERS: Partial<Record<PrimaryMode, ModeUrgencyResolver>> = {
  salesConsole: salesConsoleResolver,
};

// Exported individually for unit-testing without round-tripping through the map.
export { salesConsoleResolver };
